import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import api, { getImageUrl } from '../../utils/api';

interface Property {
    _id: string;
    title: string;
    price: number;
    city: string;
    area: string;
    status: string;
    type: string;
    images: string[];
    isActive: boolean;
    isFeatured: boolean;
}

export default function MyProperty() {
    const { token } = useAuth();
    const [properties, setProperties] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const fetchProperties = () => {
        if (!token) { setLoading(false); return; }
        setLoading(true);
        api.getMyProperties(token)
            .then(data => {
                if (Array.isArray(data)) setProperties(data);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchProperties(); }, [token]);

    const handleDelete = async (id: string) => {
        if (!token || !window.confirm('Are you sure you want to delete this property?')) return;
        setDeleting(id);
        try {
            const res = await api.deleteProperty(token, id);
            if (res.message === 'Property deleted successfully') {
                setProperties(prev => prev.filter(p => p._id !== id));
                setMsg({ type: 'success', text: 'Property removed from your portfolio.' });
            } else {
                setMsg({ type: 'error', text: res.message || 'Failed to delete property.' });
            }
        } catch {
            setMsg({ type: 'error', text: 'Server error. Please try again.' });
        }
        setDeleting(null);
    };

    return (
        <UserDashboardLayout>
            <div className="flex flex-col md:flex-row items-center justify-between mb-8">
                <div>
                    <h2 className="text-3xl font-black text-[#05144b]">My Properties</h2>
                    <p className="text-[#05144b]/70 font-medium tracking-tight uppercase text-[10px]">Manage your active listings and monitor their performance.</p>
                </div>
                <div className="mt-6 md:mt-0">
                    <Link to="/submit-property-dashboard" className="bg-blue-600 text-white px-8 py-3.5 rounded-2xl font-bold hover:shadow-xl transition-all shadow-blue-200 shadow-lg flex items-center gap-2">
                        <i className="fa-solid fa-plus-circle"></i> Create New Listing
                    </Link>
                </div>
            </div>

            {msg && (
                <div className={`p-4 rounded-2xl mb-6 flex items-center gap-3 font-bold border ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-check-circle' : 'fa-circle-exclamation'}`}></i>
                    {msg.text}
                </div>
            )}

            <div className="bg-white rounded-[2rem] border border-[#05144b]/10 shadow-sm overflow-hidden mb-20">
                <div className="p-8 border-b border-[#05144b]/5 bg-blue-50/20">
                    <h4 className="text-xl font-bold text-[#05144b] uppercase italic tracking-tighter">Active Inventory ({properties.length})</h4>
                </div>

                {loading ? (
                    <div className="text-center py-20">
                        <div className="spinner-border text-blue-600" role="status"></div>
                        <p className="mt-4 text-blue-950 font-bold tracking-widest uppercase text-[10px]">Loading Database...</p>
                    </div>
                ) : properties.length === 0 ? (
                    <div className="text-center py-20 px-10">
                        <div className="w-20 h-20 bg-blue-50 text-[#05144b]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <i className="fa-solid fa-house-chimney text-3xl"></i>
                        </div>
                        <h5 className="text-[#05144b] font-black uppercase tracking-tight italic mb-2">No Properties Found</h5>
                        <p className="text-[#05144b]/60 mb-8 max-w-sm mx-auto font-medium text-sm">You haven't submitted any listings yet. Start your journey by listing your first property today.</p>
                        <Link to="/submit-property-dashboard" className="bg-[#05144b] text-[#ffbb00] border-2 border-[#ffbb00] px-10 py-4 rounded-2xl font-black uppercase text-[10px] tracking-[3px] shadow-xl hover:-translate-y-1 inline-block transition-all active:scale-95">
                            Submit First Listing
                        </Link>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {properties.map((item) => (
                            <div key={item._id} className="p-8 hover:bg-white/50 transition-all group flex flex-col md:flex-row items-center gap-8">
                                <div className="w-full md:w-48 h-36 rounded-3xl overflow-hidden shadow-sm border border-white flex-shrink-0">
                                    {item.images && item.images.length > 0 ? (
                                        <img src={getImageUrl(item.images[0])} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt={item.title} />
                                    ) : (
                                        <div className="w-full h-full bg-blue-50 flex items-center justify-center text-blue-100">
                                            <i className="fa-solid fa-image-slash text-3xl"></i>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex-1 space-y-3 text-center md:text-left w-full">
                                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                                        <span className="px-2.5 py-0.5 rounded-full bg-blue-50 text-[#05144b] text-[8px] font-black uppercase tracking-widest border border-[#05144b]/5">{item.type}</span>
                                        <span className={`px-2.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border ${item.status === 'Ready to Move' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-50 text-amber-600 border-amber-100'}`}>
                                            {item.status}
                                        </span>
                                    </div>
                                    <h4 className="text-xl font-black text-[#05144b] hover:text-[#ffbb00] transition-colors leading-tight">
                                        <Link to={`/property-details/${item._id}`}>{item.title}</Link>
                                    </h4>
                                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-2 text-xs font-black text-[#05144b]/70 uppercase tracking-wider">
                                        <span className="text-[#05144b] text-base italic tracking-tighter font-black">₹{item.price?.toLocaleString()}</span>
                                        <span className="flex items-center gap-1.5 grayscale opacity-50">
                                            <i className="fa-solid fa-location-dot"></i>
                                            {item.city}{item.area && ` · ${item.area}`}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex flex-row md:flex-col gap-3 flex-shrink-0 w-full md:w-auto justify-center">
                                    <Link to={`/property-details/${item._id}`} className="w-12 h-12 bg-white text-blue-950 rounded-2xl flex items-center justify-center hover:bg-blue-50 hover:text-blue-600 transition-all shadow-sm">
                                        <i className="fa-solid fa-eye text-lg"></i>
                                    </Link>
                                    <button 
                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all shadow-sm ${item.isFeatured ? 'bg-amber-50 text-amber-500' : 'bg-white text-blue-950 hover:text-amber-500'}`}
                                    >
                                        <i className={`fa-${item.isFeatured ? 'solid' : 'regular'} fa-star text-lg`}></i>
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(item._id)}
                                        disabled={deleting === item._id}
                                        className="w-12 h-12 bg-rose-50 text-rose-400 rounded-2xl flex items-center justify-center hover:bg-rose-100 hover:text-rose-600 transition-all shadow-sm disabled:opacity-50"
                                    >
                                        {deleting === item._id ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-regular fa-trash-can text-lg"></i>}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </UserDashboardLayout>
    );
}


